import { useEffect, useId, useState } from 'react';
import { api, type Mes, type Noche, type Partido, type Perfil, type Yo } from '../services/api';
import { aFechaLocal, diasEntre, hoy, mesDe, sumarMeses } from '../nucleo/fechas';
import { LOGROS } from '../nucleo/logros';
import { tuNoche } from '../nucleo/progreso';
import { Boton } from './ui';

type Momento = 'noche' | 'mes' | 'historia';
export function TarjetaProgreso({ yo, perfil, pendientes, errorPerfil, onReintentar, onPerfil, onMes, onResumen }: {
  yo: Yo; perfil: Perfil | null; pendientes: Partido[];
  errorPerfil: boolean; onReintentar: () => void;
  onPerfil: () => void; onMes: () => void; onResumen: (mes: string) => void;
}) {
  const [momento, setMomento] = useState<Momento>('noche');
  const [noche, setNoche] = useState<Noche | null>(null);
  const [errorNoche, setErrorNoche] = useState(false);
  const [reintento, setReintento] = useState(0);
  const [cerrado, setCerrado] = useState<Mes | null>(null);
  const id = useId();
  const fecha = perfil?.evolucion.at(-1)?.fecha ?? null;
  const anterior = sumarMeses(mesDe(hoy()), -1);

  useEffect(() => {
    let vivo = true;
    setNoche(null); setErrorNoche(false);
    if (fecha) api.noche(fecha).then(d => vivo && setNoche(d)).catch(() => vivo && setErrorNoche(true));
    return () => { vivo = false; };
  }, [fecha, perfil, reintento]);
  useEffect(() => {
    let vivo = true;
    api.mes(anterior).then(d => vivo && setCerrado(d.cerrado ? d : null)).catch(() => vivo && setCerrado(null));
    return () => { vivo = false; };
  }, [anterior]);

  const balance = noche && perfil ? tuNoche(noche, yo.jugador.id, perfil.logros) : null;
  const sinConfirmar = pendientes.filter(p => p.jugado_el === fecha && p.jugadores.some(j => j.id === yo.jugador.id)).length;
  const idsLogros = new Set(perfil?.logros.map(l => l.logro_id));
  const reciente = perfil?.logros.reduce((a, b) => a.obtenido_en > b.obtenido_en ? a : b, perfil.logros[0]!);
  const catalogo = perfil?.logros_catalogo ?? LOGROS;
  const ultimoLogro = catalogo.find(l => l.id === reciente?.logro_id);

  return (
    <section aria-labelledby={`${id}-titulo`} className="flex flex-col gap-4 rounded-2xl border-2 border-(--color-acento)
      bg-linear-to-br from-(--color-heroe) to-(--color-panel) p-4">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-titulo`} className="font-titulo text-3xl">Tu progreso</h2>
          <p className="text-sm text-(--color-apagado)">Cada noche suma a tu historia</p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-titulo text-4xl text-(--color-acento) tabular-nums">{perfil?.jugador.elo ?? Math.round(yo.jugador.elo)}</p>
          <p className="text-xs text-(--color-apagado)">Nivel</p>
        </div>
      </header>
      <div role="tablist" aria-label="Momentos de tu progreso" className="flex rounded-xl bg-(--color-tarjeta) p-1">
        {([['noche', 'Tu noche'], ['mes', 'Tu mes'], ['historia', 'Tu historia']] as const).map(([valor, titulo], indice, lista) => (
          <button key={valor} type="button" role="tab" id={`${id}-${valor}`} aria-selected={momento === valor}
            aria-controls={`${id}-panel`} tabIndex={momento === valor ? 0 : -1}
            onClick={() => setMomento(valor)}
            onKeyDown={e => {
              const siguiente = e.key === 'ArrowRight' ? (indice + 1) % lista.length
                : e.key === 'ArrowLeft' ? (indice + lista.length - 1) % lista.length
                  : e.key === 'Home' ? 0 : e.key === 'End' ? lista.length - 1 : null;
              if (siguiente === null) return;
              e.preventDefault(); setMomento(lista[siguiente]![0]);
              document.getElementById(`${id}-${lista[siguiente]![0]}`)?.focus();
            }}
            className={`min-h-11 min-w-0 flex-1 rounded-lg px-1 text-sm transition
              ${momento === valor ? 'bg-(--color-acento) font-semibold text-(--color-sobre-acento)' : 'text-(--color-apagado)'}`}>
            {titulo}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${momento}`} className="flex flex-col gap-3">
        {!perfil ? errorPerfil ? <>
          <p className="text-sm">No pudimos traer tu progreso.</p>
          <Boton tipo="secundario" chico onClick={onReintentar}>Volver a intentar</Boton>
        </> : <p className="text-sm text-(--color-apagado)">Buscando tu progreso…</p> : momento === 'noche' ? (
          !fecha ? <>
            <p className="font-semibold">Tu historia empieza con el primer partido</p>
            <p className="text-sm text-(--color-apagado)">Cuando lo cargues, acá vas a ver cómo te fue y cuánto cambió tu nivel.</p>
          </> : errorNoche ? <>
            <p className="text-sm">No pudimos traer tu última noche.</p>
            <Boton tipo="secundario" chico onClick={() => setReintento(n => n + 1)}>Volver a intentar</Boton>
          </> : !balance ? <p className="text-sm text-(--color-apagado)">Armando tu última noche…</p> : <>
            <p className="text-xs uppercase tracking-wide text-(--color-apagado)">Tu última noche · {cuando(balance.fecha)}</p>
            <p className="font-titulo text-3xl leading-tight">
              Ganaste {balance.ganados} de {balance.jugados}
              <span className="block text-(--color-acento)">{balance.cambio > 0 ? `Subiste ${balance.cambio} de nivel`
                : balance.cambio < 0 ? `Bajaste ${-balance.cambio} de nivel` : 'Tu nivel quedó igual'}</span>
            </p>
            <p className="text-sm text-(--color-apagado)">Nivel {balance.antes} → {balance.despues} · {balance.ganados}-{balance.perdidos} en partidos</p>
            {balance.logros.length > 0 && <div className="flex flex-wrap gap-1.5" aria-label="Logros de esa noche">
              {balance.logros.map(({ id, veces }) => {
                const logro = catalogo.find(l => l.id === id);
                return logro ? <span key={id} className="rounded-full border border-(--color-borde) px-2.5 py-1 text-xs">
                  {logro.emoji} {logro.nombre}{veces > 1 && ` ×${veces}`}
                </span> : null;
              })}
            </div>}
            {sinConfirmar > 0 && <p className="text-xs text-(--color-alerta)">Incluye {sinConfirmar} {sinConfirmar === 1 ? 'partido sin confirmar' : 'partidos sin confirmar'}. Si se rechazan, el balance se recalcula.</p>}
          </>
        ) : momento === 'mes' ? <>
          <p className="text-xs uppercase tracking-wide text-(--color-apagado)">{perfil.mes?.titulo ?? 'Este mes'}</p>
          {perfil.mes ? <>
            <div className="grid grid-cols-3 gap-2">
              <Dato valor={perfil.mes.posicion ? `${perfil.mes.posicion}º` : 'Sin puesto'} titulo="Puesto del mes" />
              <Dato valor={`${perfil.mes.ganados}-${perfil.mes.perdidos}`} titulo="G-P" />
              <Dato valor={String(perfil.mes.puntos)} titulo="Puntos del mes" />
            </div>
            {!perfil.mes.elegible && <p className="text-xs text-(--color-apagado)">Todavía jugaste pocos partidos para entrar en la pelea por el campeonato.</p>}
          </> : <p className="text-sm text-(--color-apagado)">Este mes todavía no jugaste. El próximo partido empieza tu cuenta del mes.</p>}
          <Boton ancho onClick={onMes}>Ver la tabla del mes →</Boton>
          {cerrado && <Boton ancho tipo="secundario" onClick={() => onResumen(cerrado.mes)}>Ver tu resumen de {cerrado.titulo} →</Boton>}
        </> : <>
          <div className="grid grid-cols-3 gap-2">
            <Dato valor={perfil.jugador.posicion ? `${perfil.jugador.posicion}º de ${perfil.jugador.de}` : 'Sin puesto'} titulo="Puesto en Nivel" />
            <Dato valor={perfil.racha_actual === 0 ? '–' : String(Math.abs(perfil.racha_actual))}
              titulo={perfil.racha_actual > 0 ? 'Ganados seguidos' : perfil.racha_actual < 0 ? 'Perdidos seguidos' : 'Racha'} />
            <Dato valor={`${idsLogros.size}/${catalogo.filter(l => !('archivado' in l) || !l.archivado).length}`} titulo="Logros" />
          </div>
          <p className="text-sm text-(--color-apagado)">{perfil.partidos} partidos · Mejor nivel: {perfil.pico.elo} · Mejor racha: {perfil.mejor_racha}</p>
          {ultimoLogro && reciente && <p className="text-sm">Lo último: {ultimoLogro.emoji} <b>{ultimoLogro.nombre}</b> · {cuando(aFechaLocal(new Date(reciente.obtenido_en * 1000)))}</p>}
          <p className="text-xs text-(--color-apagado)">Tu evolución, con quién rendís y el cara a cara con tus rivales.</p>
        </>}
        {momento !== 'mes' && <Boton ancho onClick={onPerfil}>Ver tu historia y logros →</Boton>}
      </div>
    </section>
  );
}
function Dato({ valor, titulo }: { valor: string; titulo: string }) {
  return <div className="rounded-xl bg-(--color-tarjeta) px-1.5 py-2 text-center">
    <p className="text-base font-bold tabular-nums">{valor}</p><p className="text-[11px] text-(--color-apagado)">{titulo}</p>
  </div>;
}
function cuando(fecha: string): string {
  const dias = diasEntre(fecha, hoy());
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  const [, mes, dia] = fecha.split('-').map(Number);
  return `el ${dia}/${mes}`;
}
